import { Component, Inject } from '@angular/core';
import {
	AbstractControl,
	FormArray,
	FormBuilder,
	FormGroup,
	Validators
} from '@angular/forms';
import {
	throwError as _observableThrow,
	of as _observableOf,
	BehaviorSubject,
} from 'rxjs';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from 'src/shared/alerts/alert-dialog/alert-dialog.component';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import * as moment from 'moment';
import { bufferToBase64 } from 'src/shared/utilities/utility-functions';
import { ModernFile } from '../documents.models';
import { JobsClient, NewDocumentRequest } from 'src/shared/api';
@Component({
	selector: 'rpshared-add-document-dnd-dialog',
	templateUrl: './add-document-dnd-dialog.component.html',
	styleUrls: ['./add-document-dnd-dialog.component.scss'],
})
export class AddDocumentDragAndDropDialogComponent extends SelfDestruct {
	form: FormGroup;
	parentId: number;
	isFinished: boolean = false;
	isUploading$ = new BehaviorSubject<boolean>(false);
	uploadedStatus = 'Uploaded.';
	uploadingStatus = 'Uploading…';

	get documents() {
		return this.form.get('documents') as FormArray;
	}

	constructor(
		private readonly fb: FormBuilder,
		private readonly dialog: MatDialog,
		private readonly jobsClient: JobsClient,
		@Inject(MAT_DIALOG_DATA) public data: { parentId: number }
	) {
		super();
		this.form = fb.group({ documents: this.fb.array([]) });
		this.parentId = data.parentId;
	}

	async uploadDocument() {
		if (this.form.valid && this.form.value.documents.length > 0) {
			const documentToUploads = this.documents.controls.filter(
				(x) => x.get('error').value.length === 0
			);

			if (documentToUploads.length === 0) {
				return;
			}
			this.isUploading$.next(true);

			this.form.disable();
			const promises = documentToUploads.map(
				(control: AbstractControl) => {
					control.get('uploadStatus').setValue(this.uploadingStatus);

					const newdocument = control.value as NewDocumentRequest;
					const promise = this.jobsClient
						.addDocumentToJob(newdocument)
						.toPromise()
						.then((document) => {
							control.get('id').setValue(document.id);
							control
								.get('uploadStatus')
								.setValue(this.uploadedStatus);
							control
								.get('uploadedOn')
								.setValue(document.uploadedOn);
						})
						.catch((errors) => {
							console.log(errors);
						});
					return promise;
				}
			);

			await Promise.all(promises).then((documents) => {
				if (documents.length === documentToUploads.length) {
					this.isFinished = true;
					this.isUploading$.next(false);
				}
			});
		}
	}
	/**
	 * handle file from browsing
	 */
	fileBrowseHandler(files: Array<any>) {
		if (this.documents.controls.length + files.length > 10) {
			this.dialog
				.open(AlertDialogComponent, {
					data: {
						showFailureButton: false,
						message:
							'Only 10 documents can be added at a time. Please save changes and then upload additional documents as needed.',
					},
				})
				.afterClosed();
			return;
		}
		this.isUploading$.next(true);
		this.prepareFilesList(files);
	}

	/**
	 * Convert Files list to normal array list
	 * @param files (Files List)
	 */
	prepareFilesList(files: Array<any>) {
		const promises = [];
		for (const item of files) {
			const fileObject = item as ModernFile;
			const docRequest = from(fileObject.arrayBuffer())
				.pipe(
					map((file) => ({
						fileName: fileObject.name,
						timeStamp: moment.utc().toJSON(),
						attributes: [],
						data: bufferToBase64(new Uint8Array(file)),
					}))
				)
				.toPromise();
			promises.push(docRequest);
		}

		Promise.all(promises).then(
			(
				documentRequests: {
					fileName: string;
					timeStamp: string;
					attributes: any[];
					data: string;
				}[]
			) => {
				documentRequests.forEach(
					(document: {
						fileName: string;
						timeStamp: string;
						attributes: any[];
						data: string;
					}) => {
						this.documents.push(
							this.fb.group({
								id: [0],
								parentId: [this.parentId],
								title: [
									document.fileName,
									[
										Validators.required,
										Validators.maxLength(100),
									],
								],
								fileName: [document.fileName],
								description: [''],
								timeStamp: [document.timeStamp],
								attributes: [[]],
								data: [document.data],
								hideFromCustomer: [''],
								hideFromFieldWorker: [true],
								includeInInvoicing: [''],
								error: [this.validateDocumentUpload(document)],
								uploadStatus: [''],
								uploadedOn: [''],
							})
						);
					}
				);
				this.isUploading$.next(false);
			}
		);
	}

	getStatusClass(control: AbstractControl) {
		if (control.get('uploadStatus').value === this.uploadedStatus) {
			return 'status-success';
		}
		if (control.get('uploadStatus').value === this.uploadedStatus) {
			return 'status-inprogress';
		}
		return '';
	}

	hideFromCustomerOnChange(document: AbstractControl) {
		if (document.value.hideFromCustomer) {
			document.get('includeInInvoicing').setValue(false);
			document.get('includeInInvoicing').disable();
		} else {
			document.get('includeInInvoicing').enable();
		}
	}

	validateDocumentUpload(docRequest: {
		fileName: string;
		timeStamp: string;
		attributes: any[];
		data: string;
	}): any {
		if (docRequest.data.length === 0) {
			return 'No file data was provided for the document. Please try again!';
		}
		if (docRequest.data.length >= 104857600) {
			return 'File size is too large. Please try again!';
		}
		if (
			docRequest.fileName.trim().length === 0 ||
			docRequest.fileName.split('.')[0].trim().length === 0
		) {
			return 'No file name was provided for the document. Please try again!';
		}
		if (docRequest.fileName.split('.')[1] === undefined) {
			return 'No file extension format was provided for the document. Please try again!';
		}
		return '';
	}

	get disableUpload() {
		if (this.isUploading$.getValue()) {
			return true;
		}
		if (this.isFinished) {
			return true;
		}
		return false;
	}
}
